import React, { useState, KeyboardEventHandler, useEffect } from 'react';
import { Select, Tag, Tooltip } from 'antd';
import * as translations from '../../strings';
import { getLangKey } from '../../../../../../../javascript/models/Language';
import { PlusCircle, Tag as TagIcon } from 'lucide-react';
import * as Types from '../../types';
import cx from 'classnames';
import styles from '../index.module.css';
import { Sparkles } from '../../../../../../../redesign/javascript/components/Sparkles';
import AiLoadingIcon from '../../../../../../../redesign/javascript/components/AiLoadingIcon';
import track from '../../../../../../../redesign/javascript/utils/analytics';

type Props = {
  container: Element;
  tags: Types.Tag[];
  onTagsChange: (tags: Types.Tag[]) => void;
  availableTags: Types.Tag[];
  inputSize: 'large' | 'middle';
  createTagsViaNewTask: boolean;
  isFetchingTagSuggestions?: boolean;
  apiDomain: string;
};

const strings = translations[getLangKey()];
const { Option } = Select;

const Tags = (props: Props) => {
  const {
    container,
    availableTags,
    tags,
    onTagsChange,
    inputSize,
    createTagsViaNewTask,
    isFetchingTagSuggestions = false,
    apiDomain
  } = props;

  const availableTagIds = availableTags.map(({ id }) => id);

  useEffect(() => {
    if (tags.length && availableTagIds.length) {
      const isValidTag = (id: number): boolean =>
        (id < 1 && createTagsViaNewTask) || availableTagIds.includes(id);

      const hasInvalidTags = tags.find(({ id }) => {
        return !isValidTag(id);
      });

      if (hasInvalidTags) {
        onTagsChange(tags.filter(({ id }) => isValidTag(id)));
      }
    }
  }, [availableTagIds, createTagsViaNewTask]);

  const [showPlaceholder, setShowPlaceholder] = useState<boolean>(true);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [newTag, setNewTag] = useState<Types.Tag | undefined>();
  const [searchValue, setSearchValue] = useState<string>();

  const addedTags = tags.filter((tag: Types.Tag) => tag.id < 1);
  const hasAddedTags = !!addedTags.length;

  const getSelectableTags = () => {
    const selectableTags = availableTags
      .slice()
      .sort((a, b) => a.display.localeCompare(b.display));
    if (hasAddedTags) {
      addedTags.forEach((tag: Types.Tag) => selectableTags.push(tag));
    }

    const isAdded = tags.find(tag => tag.display === newTag?.display);
    const matchedSubstring = (_newTagDisplay: string) =>
      availableTags.filter(tag => {
        const display = tag.display.toLowerCase();
        const newDisplay = _newTagDisplay.toLowerCase();
        return (
          display.length > newDisplay.length && display.includes(newDisplay)
        );
      }).length;

    if (newTag?.display && !isAdded && matchedSubstring(newTag.display) > 0) {
      selectableTags.push(newTag);
    }
    return selectableTags;
  };

  const handleTagsFocus = () => {
    if (!tags.length) {
      setShowPlaceholder(!showPlaceholder);
    }
  };

  const handleTagsBlur = () => {
    handleTagsFocus();
    setNewTag(undefined);
    setSearchValue('');
  };

  const removeTag = (value: string) => {
    let newTagsToSet: Types.Tag[] = [];
    const tagToRemove = tags.find((tag: Types.Tag, index: number) => {
      if (tag.display !== value) return false;
      newTagsToSet = [...tags.slice(0, index), ...tags.slice(index + 1)];
      return true;
    });
    if (tagToRemove?.aiSuggestion) {
      track(
        'Ai Tag Removed',
        { removed: tagToRemove.display },
        {},
        apiDomain
      );
    }
    onTagsChange(newTagsToSet);
  };

  const tagOptions = ({ id, display }: { id: number; display: string }) => {
    const isNewTag = display === newTag?.display && newTag?.id === id;
    return (
      <Option
        className={styles.tagOption}
        value={display}
        label={display}
        key={id + display}
      >
        {isNewTag
          ? React.createElement(PlusCircle as any, {
              className: styles.newTagIcon
            })
          : React.createElement(TagIcon as any, {
              className: styles.optionIcon
            })}
        <span title={display} className={styles.optionText}>
          {display}
        </span>
      </Option>
    );
  };

  const getTags = (renderProps: any) => {
    const isAiSuggestion = !!tags.find(t => t.display === renderProps.value)
      ?.aiSuggestion;
    return (
      <Tag
        className={cx(styles.tag, {
          [styles.aiSuggestion]: isAiSuggestion
        })}
        icon={
          isAiSuggestion ? (
            <Sparkles className={styles.tagIcon} linearGradient />
          ) : (
            React.createElement(TagIcon as any, { className: styles.tagIcon })
          )
        }
        closable
        onClose={() => removeTag(renderProps.value)}
      >
        {renderProps.label[1]}
      </Tag>
    );
  };

  const handleTagsChange = (tagDisplays: string[]) => {
    const selectableTags = getSelectableTags();
    const updatedTags = selectableTags.filter(
      (tag: Types.Tag) =>
        !!tagDisplays.find((display: string) => display === tag.display)
    );
    onTagsChange(updatedTags);
    setSearchValue('');
    setNewTag(undefined);
  };

  const handleTagSearch = (search: string) => {
    setSearchValue(search);
    if (createTagsViaNewTask) {
      setNewTag({ id: -1, display: search });
    }
  };

  const handleAddNewTag = () => {
    setSearchValue('');
    const tagsClone = tags.slice();
    if (newTag && newTag.display) {
      tagsClone.push(newTag);
    }
    onTagsChange(tagsClone);
    setNewTag(undefined);
  };

  const handleTagInputKeyDown: KeyboardEventHandler<HTMLInputElement> = event => {
    const isEnter = event.keyCode === 13;
    if (isEnter && newTag && newTag.display) {
      handleAddNewTag();
    }
  };

  const renderTagOptions = () =>
    getSelectableTags().map((tag: Types.Tag) => tagOptions(tag));

  const handleOnSelect = () => {
    if (dropdownOpen) setDropdownOpen(!dropdownOpen);
  };

  const tagRenderWrapper = (p: any) => getTags(p);

  return (
    <Select
      mode="multiple"
      placeholder={
        showPlaceholder ? (
          <span className={styles.placeHolder}>{strings.tags}</span>
        ) : null
      }
      className={cx(styles.select, styles.selectTags, {
        [styles.hasValue]: tags.length,
        [styles.aiLoading]: isFetchingTagSuggestions
      })}
      // @ts-expect-error
      getPopupContainer={() => container}
      onChange={handleTagsChange}
      value={tags.map(({ display }: { display: string }) => display)}
      onFocus={handleTagsFocus}
      onBlur={handleTagsBlur}
      // @ts-expect-error
      tagRender={tagRenderWrapper}
      onSearch={handleTagSearch}
      searchValue={searchValue}
      open={dropdownOpen}
      suffixIcon={
        isFetchingTagSuggestions ? (
          <Tooltip
            title={
              <span>
                Thinking
                <span className={styles.aiDots} aria-hidden="true">
                  <span className={styles.aiDot}>.</span>
                  <span className={styles.aiDot}>.</span>
                  <span className={styles.aiDot}>.</span>
                </span>
              </span>
            }
            getPopupContainer={() =>
              (container as HTMLElement) || document.body
            }
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                pointerEvents: 'auto'
              }}
            >
              {React.createElement(AiLoadingIcon as any, { size: 20 })}
            </span>
          </Tooltip>
        ) : null
      }
      onSelect={handleOnSelect}
      notFoundContent={
        newTag ? (
          <div onClick={handleAddNewTag} className={styles.newTagOuter}>
            {React.createElement(PlusCircle as any, {
              className: styles.newTagIcon
            })}
            <span className={styles.optionText}>{newTag.display}</span>
          </div>
        ) : (
          <div className={styles.noTags}>{strings.noTagsFound}</div>
        )
      }
      onInputKeyDown={handleTagInputKeyDown}
      size={inputSize}
      onDropdownVisibleChange={setDropdownOpen}
    >
      {renderTagOptions()}
    </Select>
  );
};

export default Tags;
